import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Button,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Check as CheckIcon,
  Science as ScienceIcon,
  CameraAlt as CameraIcon,
  Settings as SettingsIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  FilterDrama as FilterIcon,
  CloudQueue as CloudIcon,
  Visibility as VisibilityIcon,
  CenterFocusStrong as FocusIcon,
} from '@mui/icons-material';
import { apiService } from '../../../services/apiService';
import { parseRA, parseDec } from '../../../utils/coordinateFormatting';

const ReviewStep = ({ watch, onSubmit, isSubmitting, setValue, editingId }) => {
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [riseToSetWindow, setRiseToSetWindow] = useState(null);
  const [riseToSetLoading, setRiseToSetLoading] = useState(false);
  const [riseToSetError, setRiseToSetError] = useState(null);

  const formData = watch();
  const { 
    observationName,
    targetName, 
    ra, 
    dec, 
    priority, 
    targets = [],
    exposures = [],
    sequenceRepeatCount = 1,
    minAltitude,
    maxAirmass,
    maxMoonPhase,
    maxClouds,
    twilightType,
    startTime,
    endTime,
    notes,
    useDefaults,
    afEnabled,
    initialAutofocus,
    afTimeBased,
    afTimeInterval,
    afTempBased,
    afTempThreshold,
    afHfrBased,
    afHfrPercentage,
    afFilterChange,
    afAfterExposures,
    afAfterExposuresCount
  } = formData;
  
  // Check if this is a multi-target observation
  const isMultiTarget = targets && targets.length > 0;

  // Calculate total imaging time
  const getTotalImagingTime = () => {
    let totalSeconds = 0;
    
    if (isMultiTarget) {
      // For multi-target, sum up all target exposure times
      targets.forEach(target => {
        const targetExposures = target.exposureConfigs || [];
        const targetRepeat = target.sequenceRepeatCount || 1;
        const targetSeconds = targetExposures.reduce((sum, exp) => {
          return sum + ((exp.exposureTime || 0) * (exp.exposureCount || 0));
        }, 0);
        totalSeconds += targetSeconds * targetRepeat;
      });
    } else {
      // For single target, use the main exposures
      totalSeconds = exposures.reduce((sum, exp) => {
        return sum + ((exp.exposureTime || 0) * (exp.exposureCount || 0));
      }, 0);
      totalSeconds = totalSeconds * sequenceRepeatCount;
    }
    
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const getTotalFrames = () => {
    if (isMultiTarget) {
      return targets.reduce((total, target) => {
        const targetExposures = target.exposureConfigs || [];
        const targetRepeat = target.sequenceRepeatCount || 1;
        const targetFrames = targetExposures.reduce((sum, exp) => sum + (parseInt(exp.exposureCount) || 0), 0);
        return total + (targetFrames * targetRepeat);
      }, 0);
    } else {
      const baseFrames = exposures.reduce((sum, exp) => sum + (parseInt(exp.exposureCount) || 0), 0);
      return baseFrames * sequenceRepeatCount;
    }
  };

  const getPriorityColor = (priority) => {
    if (priority >= 8) return 'error';
    if (priority >= 5) return 'warning';
    return 'success';
  };

  const getPriorityLabel = (priority) => {
    if (priority >= 8) return 'High Priority';
    if (priority >= 5) return 'Medium Priority';
    return 'Low Priority';
  };

  const formatCoordinate = (coord, isRA) => {
    if (coord === undefined || coord === null || coord === '') return 'Not specified';

    // The form stores manually-typed coordinates as raw sexagesimal strings
    // (e.g. "18:37:25.95"); SIMBAD lookups store decimal-degree strings. Use the
    // shared parsers so both formats convert to decimal degrees — plain
    // parseFloat() would stop at the first colon and silently keep only the
    // integer degrees, mangling the display (see #coordinate-review-bug).
    const numCoord = isRA ? parseRA(coord) : parseDec(coord);
    if (isNaN(numCoord)) return 'Not specified';
    
    if (isRA) {
      // RA is stored in decimal degrees (0-360), convert to hours (0-24)
      const totalHours = numCoord / 15;
      const hours = Math.floor(totalHours);
      const minutesDecimal = (totalHours - hours) * 60;
      const minutes = Math.floor(minutesDecimal);
      const seconds = (minutesDecimal - minutes) * 60;
      return `${hours}h ${minutes}m ${seconds.toFixed(1)}s`;
    } else {
      // Dec is stored in decimal degrees (-90 to +90)
      const sign = numCoord >= 0 ? '+' : '-';
      const absDec = Math.abs(numCoord);
      const degrees = Math.floor(absDec);
      const minutesDecimal = (absDec - degrees) * 60;
      const minutes = Math.floor(minutesDecimal);
      const seconds = (minutesDecimal - minutes) * 60;
      return `${sign}${degrees}° ${minutes}' ${seconds.toFixed(1)}"`;
    }
  };

  const handleSubmitClick = () => {
    setConfirmDialog(true);
  };

  const handleConfirmSubmit = () => {
    setConfirmDialog(false);
    // If rise-to-set, set the calculated times before submitting
    if (formData.observationType === 'rise_to_set' && riseToSetWindow) {
      setValue('fixedTimeStart', new Date(riseToSetWindow.start_time));
      setValue('fixedTimeEnd', new Date(riseToSetWindow.end_time));
      setValue('observationType', 'fixed_time'); // Convert to fixed_time for backend
    }
    onSubmit();
  };

  // Calculate rise-to-set window when observation type changes
  useEffect(() => {
    const calculateRiseToSet = async () => {
      // Only calculate if observation type is rise_to_set
      if (formData.observationType !== 'rise_to_set') {
        setRiseToSetWindow(null);
        return;
      }

      // Check if we have all required data
      if (!formData.ra || !formData.dec || !formData.assignedTo || !formData.riseToSetDate) {
        setRiseToSetWindow(null);
        return;
      }

      setRiseToSetLoading(true);
      setRiseToSetError(null);

      try {
        console.log('Calculating rise-to-set window...');
        const response = await apiService.post('/observations/calculate-rise-to-set', {
          target: {
            ra: parseRA(formData.ra),
            dec: parseDec(formData.dec),
            name: formData.targetName || 'Target'
          },
          observatory_id: formData.assignedTo,
          date: formData.riseToSetDate.toISOString(),
          options: {
            min_altitude: formData.riseToSetMinAltitude || 30,
            twilight_type: formData.riseToSetTwilightType || 'astronomical',
            respect_twilight: true,
            safety_margin_minutes: formData.riseToSetSafetyMargin || 0
          }
        });

        if (response.data.success) {
          setRiseToSetWindow(response.data.window);
          console.log('Rise-to-set window calculated:', response.data.window);
        } else {
          setRiseToSetError(response.data.error || 'Failed to calculate window');
        }
      } catch (error) {
        console.error('Error calculating rise-to-set window:', error);
        setRiseToSetError(error.response?.data?.error?.message || error.message || 'Failed to calculate observation window');
      } finally {
        setRiseToSetLoading(false);
      }
    };

    calculateRiseToSet();
  }, [formData.observationType, formData.ra, formData.dec, formData.assignedTo, formData.riseToSetDate, formData.riseToSetMinAltitude, formData.riseToSetTwilightType, formData.riseToSetSafetyMargin, formData.targetName]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review Your Observation
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Please review all the details before submitting your observation request.
      </Typography>

      {/* Observation Name */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScienceIcon />
          {(() => {
            // Show what the observation name will be
            if (observationName && observationName.trim() !== '') {
              return observationName;
            } else if (targets && targets.length === 1) {
              return targets[0].name;
            } else if (targets && targets.length > 1) {
              const targetNames = targets.slice(0, 3).map(t => t.name).join(', ');
              if (targets.length > 3) {
                return `${targetNames}, and ${targets.length - 3} more`;
              } else {
                return targetNames;
              }
            }
            return 'Unnamed Observation';
          })()}
        </Typography>
        {!observationName && (
          <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mt: 0.5 }}>
            (Auto-generated from target name{targets && targets.length > 1 ? 's' : ''})
          </Typography>
        )}
      </Paper>

      {/* Rise-to-Set Calculated Window */}
      {formData.observationType === 'rise_to_set' && (
        <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: 'info.main', color: 'info.contrastText' }}>
          <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon />
            📅 Rise-to-Set Observation Window
          </Typography>
          <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.3)' }} />

          {riseToSetLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} sx={{ color: 'info.contrastText' }} />
              <Typography>Calculating observation window...</Typography>
            </Box>
          )}

          {riseToSetError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <strong>Calculation Error:</strong> {riseToSetError}
            </Alert>
          )}

          {riseToSetWindow && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Target will be observed from rise to set, respecting twilight and altitude constraints
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>Start Time (UTC)</Typography>
                <Typography variant="h6">
                  {new Date(riseToSetWindow.start_time).toLocaleString('en-US', {
                    timeZone: 'UTC',
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZoneName: 'short'
                  })}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  Reason: {riseToSetWindow.start_reason === 'twilight_end' ? 'Twilight ends' : 'Target rises'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>End Time (UTC)</Typography>
                <Typography variant="h6">
                  {new Date(riseToSetWindow.end_time).toLocaleString('en-US', {
                    timeZone: 'UTC',
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZoneName: 'short'
                  })}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  Reason: {riseToSetWindow.end_reason === 'twilight_start' ? 'Twilight begins' : 'Target sets'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.3)' }} />
                <Typography variant="body1" sx={{ mt: 1 }}>
                  <strong>Total Duration:</strong> {riseToSetWindow.duration_hours.toFixed(2)} hours
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                  Minimum Altitude: {riseToSetWindow.min_altitude}° |
                  Twilight: {riseToSetWindow.twilight_type}
                </Typography>
              </Grid>
            </Grid>
          )}

          {!riseToSetLoading && !riseToSetError && !riseToSetWindow && (
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Window will be calculated when all required fields are provided
            </Typography>
          )}
        </Paper>
      )}

      {/* Target Information */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScienceIcon color="primary" />
          Target Information {isMultiTarget && `(${targets.length} targets)`}
        </Typography>
        <Divider sx={{ my: 2 }} />
        
        {isMultiTarget ? (
          // Multi-target display
          <>
            {targets.map((target, index) => (
              <Box key={index} sx={{ mb: index < targets.length - 1 ? 2 : 0 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Target {index + 1}: {target.name || 'Unnamed'}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Right Ascension</Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationIcon fontSize="small" color="action" />
                      {formatCoordinate(target.ra, true)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Declination</Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationIcon fontSize="small" color="action" />
                      {formatCoordinate(target.dec, false)}
                    </Typography>
                  </Grid>
                </Grid>
                {index < targets.length - 1 && <Divider sx={{ my: 2 }} />}
              </Box>
            ))}
          </>
        ) : (
          // Single target display
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Target Name</Typography>
              <Typography variant="body1" fontWeight="medium">{targetName || 'Not specified'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Priority</Typography>
              <Chip 
                label={`${priority} - ${getPriorityLabel(priority)}`}
                color={getPriorityColor(priority)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Right Ascension</Typography>
              <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon fontSize="small" color="action" />
                {formatCoordinate(ra, true)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">Declination</Typography>
              <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon fontSize="small" color="action" />
                {formatCoordinate(dec, false)}
              </Typography>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Exposure Plan */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CameraIcon color="primary" />
          Exposure Plan
        </Typography>
        <Divider sx={{ my: 2 }} />
        
        {isMultiTarget ? (
          // Multi-target exposure display
          <>
            {targets.map((target, targetIndex) => (
              <Box key={targetIndex} sx={{ mb: targetIndex < targets.length - 1 ? 3 : 0 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {target.name || `Target ${targetIndex + 1}`} Exposures
                </Typography>
                {target.sequenceRepeatCount > 1 && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    Sequence will be repeated {target.sequenceRepeatCount} times
                  </Alert>
                )}
                <List dense>
                  {(target.exposureConfigs || []).map((exposure, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <FilterIcon color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${exposure.filter || 'No filter'} - ${exposure.exposureTime}s × ${exposure.exposureCount}`}
                        secondary={`Binning: ${exposure.binning}, Gain: ${exposure.gain}`}
                      />
                      <Chip 
                        label={`${(exposure.exposureTime * exposure.exposureCount)}s`}
                        size="small"
                        variant="outlined"
                      />
                    </ListItem>
                  ))}
                </List>
                {targetIndex < targets.length - 1 && <Divider sx={{ my: 2 }} />}
              </Box>
            ))}
            
            <Box sx={{ mt: 2, p: 2, backgroundColor: 'background.default', borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Total Frames</Typography>
                  <Typography variant="h6">{getTotalFrames()}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Total Imaging Time</Typography>
                  <Typography variant="h6">{getTotalImagingTime()}</Typography>
                </Grid>
              </Grid>
            </Box>
          </>
        ) : (
          // Single target exposure display
          <>
            {exposures.length === 0 ? (
              <Alert severity="warning">No exposures defined</Alert>
            ) : (
              <>
                {sequenceRepeatCount > 1 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Sequence will be repeated {sequenceRepeatCount} times
                  </Alert>
                )}
                <List dense>
                  {exposures.map((exposure, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <FilterIcon color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${exposure.filter || 'No filter'} - ${exposure.exposureTime}s × ${exposure.exposureCount}`}
                        secondary={`Binning: ${exposure.binning}, Gain: ${exposure.gain}`}
                      />
                      <Chip 
                        label={`${(exposure.exposureTime * exposure.exposureCount)}s`}
                        size="small"
                        variant="outlined"
                      />
                    </ListItem>
                  ))}
                </List>
                
                <Box sx={{ mt: 2, p: 2, backgroundColor: 'background.default', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Total Frames</Typography>
                      <Typography variant="h6">{getTotalFrames()}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">Total Imaging Time</Typography>
                      <Typography variant="h6">{getTotalImagingTime()}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </>
            )}
          </>
        )}
      </Paper>

      {/* Constraints */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon color="primary" />
          Observation Constraints
        </Typography>
        <Divider sx={{ my: 2 }} />
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2" color="text.secondary">Altitude</Typography>
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VisibilityIcon fontSize="small" color="action" />
              ≥ {minAltitude || 30}°
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2" color="text.secondary">Airmass</Typography>
            <Typography variant="body1">
              ≤ {maxAirmass || 2.0}
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2" color="text.secondary">Moon Phase</Typography>
            <Typography variant="body1">
              ≤ {maxMoonPhase || 100}%
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2" color="text.secondary">Cloud Cover</Typography>
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CloudIcon fontSize="small" color="action" />
              ≤ {maxClouds || 30}%
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2" color="text.secondary">Twilight</Typography>
            <Typography variant="body1">
              {twilightType || 'astronomical'}
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2" color="text.secondary">Date Range</Typography>
            <Typography variant="body1">
              {startTime ? new Date(startTime).toLocaleDateString('en-US') : 'Any time'}
              {endTime && ` - ${new Date(endTime).toLocaleDateString('en-US')}`}
            </Typography>
          </Grid>
        </Grid>

        {notes && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: 'background.default', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">Additional Notes</Typography>
            <Typography variant="body1">{notes}</Typography>
          </Box>
        )}
      </Paper>

      {/* Autofocus Settings */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FocusIcon color="primary" />
          Autofocus Settings
        </Typography>
        <Divider sx={{ my: 2 }} />
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">Configuration</Typography>
            <Typography variant="body1">
              {useDefaults ? 'Using observatory defaults' : 'Custom settings'}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">Plugin-managed Autofocus</Typography>
            <Typography variant="body1">
              {afEnabled === false ? 'No - managed externally' : 'Yes'}
            </Typography>
          </Grid>

          {afEnabled !== false && (
            <>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Initial Autofocus</Typography>
                <Typography variant="body1">
                  {initialAutofocus ? 'Yes - at observation start' : 'No'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Periodic Autofocus</Typography>
                <Typography variant="body1">
                  {afTimeBased ? `Every ${afTimeInterval || 30} minutes` : 'Disabled'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Temperature-based Autofocus</Typography>
                <Typography variant="body1">
                  {afTempBased ? `On ${afTempThreshold || 2}°C change` : 'Disabled'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">HFR-based Autofocus</Typography>
                <Typography variant="body1">
                  {afHfrBased ? `On ${afHfrPercentage || 5}% HFR increase` : 'Disabled'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Autofocus on Filter Change</Typography>
                <Typography variant="body1">
                  {afFilterChange ? 'Enabled' : 'Disabled'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Autofocus After N Exposures</Typography>
                <Typography variant="body1">
                  {afAfterExposures ? `Every ${afAfterExposuresCount || 10} exposures` : 'Disabled'}
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Validation Messages */}
      {((!targets || targets.length === 0) || 
        (targets && targets.some(t => !t.name || !t.ra || !t.dec || !t.exposureConfigs || t.exposureConfigs.length === 0))) && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="medium" gutterBottom>
            Please complete the following before submitting:
          </Typography>
          <List dense>
            {(!targets || targets.length === 0) && (
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="error" />
                </ListItemIcon>
                <ListItemText primary="At least one target is required" />
              </ListItem>
            )}
            {targets && targets.some(t => !t.name) && (
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="error" />
                </ListItemIcon>
                <ListItemText primary="All targets must have names" />
              </ListItem>
            )}
            {targets && targets.some(t => !t.ra || !t.dec) && (
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="error" />
                </ListItemIcon>
                <ListItemText primary="All targets must have coordinates (RA/Dec)" />
              </ListItem>
            )}
            {targets && targets.some(t => !t.exposureConfigs || t.exposureConfigs.length === 0) && (
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="error" />
                </ListItemIcon>
                <ListItemText primary="All targets must have at least one exposure configuration" />
              </ListItem>
            )}
          </List>
        </Alert>
      )}

      {/* Submit Button */}
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmitClick}
          disabled={!targets || targets.length === 0 || targets.some(t => !t.name || !t.ra || !t.dec || !t.exposureConfigs || t.exposureConfigs.length === 0) || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : <CheckIcon />}
        >
          {isSubmitting ? (editingId ? 'Updating...' : 'Submitting...') : (editingId ? 'Update Observation' : 'Submit Observation')}
        </Button>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>{editingId ? 'Confirm Observation Update' : 'Confirm Observation Submission'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to {editingId ? 'update' : 'submit'} an observation request for <strong>{observationName || (targets && targets.length > 0 ? targets[0].name : 'Unknown')}</strong>
            {formData.observationType === 'time_based' ? (
              <> with a desired observing time of {formData.desiredObservingTimeMinutes} minutes (minimum {formData.minimumObservingTimeMinutes} minutes). Exposures will repeat to fill the allocated time.</>
            ) : formData.observationType === 'fixed_time' && formData.fillTime ? (
              <> during a fixed time window. Exposures will repeat to fill the available time.</>
            ) : (
              <> with {getTotalFrames()} frames totaling {getTotalImagingTime()} of imaging time.</>
            )}
            <br /><br />
            {editingId
              ? 'The existing observation will be updated with these changes.'
              : 'Once submitted, your observation will be queued for execution based on its priority and constraints.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmSubmit} variant="contained" autoFocus>
            {editingId ? 'Confirm Update' : 'Confirm Submission'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReviewStep;